import { ExternalLinkIcon } from '@chakra-ui/icons';
import { Flex, Image, Link, Spinner, Stack, Text } from '@chakra-ui/react';
import makeBlockie from 'ethereum-blockies-base64';
import NextLink from 'next/link';
import React from 'react';
import { DEFAULT_NETWORK } from '../web3/constants';
import { formatAddress, formatToken, getTransactionUrl } from '../web3/helpers';
import { useENS } from '../web3/hooks/useENS';

type User = {
  id: string;
};

type Bid = {
  id: string;
  transactionHash: string;
  amount: string;
  bidder: User;
  bidType: string;
  createdAtTimestamp: string;
};

interface BidListProps {
  currentBid?: Bid;
  previousBids?: Bid[];
  max?: number;
  opts: Record<string, string>;
}

interface BidItemProps {
  bid: Bid;
  opts: Record<string, string>;
}

const sortedPreviousBids = (bids: BidListProps['previousBids']) =>
  bids
    ?.slice()
    .sort((a, b) => {
      return Number(b.createdAtTimestamp) - Number(a.createdAtTimestamp);
    })
    .filter((bid) => bid.bidType !== 'Final'); // dont want final bids to be in previous bids as we already use final bid as current bid

const BidListItem: React.FC<BidItemProps> = ({ bid, opts }) => {
  const { ens, avatar, loading } = useENS({ address: bid?.bidder.id ?? '' });

  const bidderLabel = ens ?? formatAddress(bid?.bidder.id);
  const bidderAvatar = avatar ?? makeBlockie(bid?.bidder.id ?? '');

  return (
    <Flex
      justify="space-between"
      px="3"
      py="1"
      rounded="md"
      {...opts}
      fontSize={{ base: 'sm', md: 'md' }}
    >
      <Flex align="center">
        {loading ? (
          <Spinner thickness="2px" speed="0.56s" emptyColor="teal.50" size="sm" color="teal" />
        ) : (
          <Image
            borderRadius="full"
            boxSize={{ base: '16px', md: '24px' }}
            src={bidderAvatar}
            fallbackSrc={makeBlockie(bid?.bidder.id ?? '')}
          />
        )}

        <Text pl="2">{bidderLabel}</Text>
      </Flex>
      <Flex align="center" justify="center">
        <Text pr="3">Ξ {Number(formatToken(bid?.amount))}</Text>
        <NextLink href={`${getTransactionUrl(DEFAULT_NETWORK, bid?.transactionHash)}`} passHref>
          <Link
            isExternal
            color="brand.teal"
            textDecoration="underline"
            aria-label="See bid on etherscan"
            textAlign="center"
            mt={-1}
          >
            <ExternalLinkIcon />
          </Link>
        </NextLink>
      </Flex>
    </Flex>
  );
};

const BidList: React.FC<BidListProps> = ({ currentBid, previousBids, max, opts }) => {
  const renderPreviousBids = (bids): React.FC<BidItemProps> => {
    return bids?.map((bid, index) => {
      return <BidListItem bid={bid} key={index} opts={opts} />;
    });
  };

  return (
    <Stack spacing={2} pt="6">
      {currentBid && <BidListItem bid={currentBid} opts={opts} />}
      {max
        ? previousBids && renderPreviousBids(sortedPreviousBids(previousBids).slice(0, max - 1))
        : previousBids && renderPreviousBids(sortedPreviousBids(previousBids))}
    </Stack>
  );
};

export default BidList;
